import React, { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, ChevronUp, Mic, MicOff, Volume2, VolumeX, EyeOff, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useCallerInPeer from "../lib/useCallerInPeer";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * RadioCallerQueuePanel — host studio side of caller-in.
 *
 * Polls `GET /api/radio/host/{slug}/call-queue` every 4s and renders:
 *  - the currently on-air caller (if any) with a big "Hang up" button
 *  - the waiting list, sorted (priority desc, joined_at asc)
 *
 * Each waiter has two actions:
 *   ▸ Promote   — take them on air (hangs up any current caller first)
 *   ▸ Drop      — remove from queue (e.g. spammers)
 *
 * Skip-the-queue tippers float to the top automatically — they're tagged
 * with a tiny "TIPPED $5" gold pill so the host knows why they jumped.
 *
 * NOTE: This component only manages the queue + state. The actual audio
 * routing (caller mic → host's broadcast mix) is wired separately
 * through the existing WebRTC announcer FX pipeline (Phase 2).
 */
export default function RadioCallerQueuePanel({ stationSlug, className = "" }) {
  const [queue, setQueue] = useState([]);
  const [onair, setOnair] = useState(null);
  const [busy, setBusy] = useState(null); // id currently being acted on
  // Whether the host has muted the caller's incoming audio (local only).
  const [callerMuted, setCallerMuted] = useState(false);
  // Host-side mic stream for private-mode talk-back. Null = listening only.
  const [hostMicStream, setHostMicStream] = useState(null);
  const audioRef = useRef(null);
  const { token } = useAuth();

  // Tear down any host-mic stream when the active caller goes away so we
  // don't keep the mic open between calls.
  useEffect(() => {
    if (!onair && hostMicStream) {
      try { hostMicStream.getTracks().forEach((t) => t.stop()); } catch {}
      setHostMicStream(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onair?.id]);

  // ── WebRTC voice bridge — host side ──
  // Active any time there's a caller in onair OR private status. When the
  // host has captured a mic for talk-back, that stream is also sent to
  // the caller through the same peer connection (bidirectional during
  // private mode).
  useCallerInPeer({
    enabled: !!onair,
    role: "host",
    stationSlug,
    token: token || "",
    apiBase: API_BASE,
    localStream: hostMicStream, // null until host enters private mode with mic on
    onRemoteStream: (stream) => {
      if (audioRef.current) {
        audioRef.current.srcObject = stream;
        audioRef.current.play().catch(() => {});
      }
    },
    onConnected: () => toast.success("Caller voice connected"),
  });

  const isPrivate = onair?.status === "private";

  // Capture the host's mic (or release it). Used for private-mode talk-back.
  const toggleHostMic = async () => {
    if (hostMicStream) {
      try { hostMicStream.getTracks().forEach((t) => t.stop()); } catch {}
      setHostMicStream(null);
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
      setHostMicStream(s);
    } catch {
      toast.error("Mic blocked. Allow microphone access to talk back.");
    }
  };

  // Mute the caller's incoming audio locally (doesn't affect the actual
  // peer — server keeps relaying — but the host doesn't hear them).
  const toggleCallerMute = () => {
    setCallerMuted((cur) => {
      const next = !cur;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  };

  useEffect(() => {
    if (!stationSlug) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/radio/host/${stationSlug}/call-queue`);
        if (!alive) return;
        const items = data?.items || [];
        setQueue(items.filter((c) => c.status === "waiting"));
        setOnair(data?.current_onair || null);
      } catch { /* keep state */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [stationSlug]);

  const act = async (cid, action) => {
    setBusy(cid);
    try {
      await api.post(`/radio/host/${stationSlug}/call-queue/${cid}/${action}`);
      // Optimistic refresh
      const { data } = await api.get(`/radio/host/${stationSlug}/call-queue`);
      setQueue((data?.items || []).filter((c) => c.status === "waiting"));
      setOnair(data?.current_onair || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="radio-caller-queue-panel" className={`bg-zinc-950 border border-white/10 rounded-xl p-3 space-y-3 ${className}`}>
      {/* Hidden audio element — sink for the caller's incoming voice. We
          deliberately don't show controls; the host hears it through their
          monitor speakers / headphones and can decide whether to route it
          into their broadcast (e.g. via OBS audio capture). */}
      <audio ref={audioRef} autoPlay data-testid="radio-caller-audio-sink" style={{ display: "none" }} />

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-black flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5" /> Caller Queue
        </div>
        <span className="text-[10px] text-white/50 font-bold">{queue.length} waiting</span>
      </div>

      {/* Current ON AIR / PRIVATE caller — big highlight so the host can't miss it */}
      {onair && (
        <div data-testid="radio-caller-onair" className={`rounded-lg border p-3 space-y-2 ${
          isPrivate ? "border-amber-400/50 bg-amber-500/10" : "border-red-500/40 bg-red-500/10"
        }`}>
          <div className="flex items-center gap-3">
            {onair.user_avatar
              ? <img src={onair.user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
              : <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPrivate ? "bg-amber-500/30" : "bg-red-500/30"}`}>
                  <Mic className={`w-5 h-5 ${isPrivate ? "text-amber-200" : "text-red-200"}`} />
                </div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-white text-sm">{onair.user_name}</span>
                {isPrivate ? (
                  <span data-testid="radio-caller-private-pill" className="text-[8px] uppercase tracking-wider font-black bg-amber-500 text-black px-1 py-px rounded">private</span>
                ) : (
                  <span className="text-[8px] uppercase tracking-wider font-black bg-red-600 text-white px-1 py-px rounded animate-pulse">on air</span>
                )}
              </div>
              <div className="text-[11px] text-white/60 line-clamp-1">
                {onair.city ? `${onair.city} · ` : ""}{onair.topic || (isPrivate ? "Whisper mode" : "On the air")}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <button
              data-testid="radio-caller-mute"
              onClick={toggleCallerMute}
              title={callerMuted ? "Unmute caller in your headphones" : "Mute caller (kills only your monitor)"}
              className={`text-[10px] font-black px-2 py-1.5 rounded-full inline-flex items-center gap-1 transition ${
                callerMuted ? "bg-white/15 text-white" : "bg-white/5 text-white/80 hover:bg-white/10"
              }`}
            >
              {callerMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              {callerMuted ? "Muted" : "Mute"}
            </button>
            {isPrivate ? (
              <button
                data-testid="radio-caller-take-public"
                onClick={() => act(onair.id, "take_public")}
                disabled={busy === onair.id}
                title="Take this caller public — listeners will hear them"
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-[10px] font-black px-2 py-1.5 rounded-full inline-flex items-center gap-1"
              >
                <Eye className="w-3.5 h-3.5" /> Take public
              </button>
            ) : (
              <button
                data-testid="radio-caller-private"
                onClick={() => act(onair.id, "private")}
                disabled={busy === onair.id}
                title="Pull this caller off-air for a private conversation. Listeners won't hear."
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[10px] font-black px-2 py-1.5 rounded-full inline-flex items-center gap-1"
              >
                <EyeOff className="w-3.5 h-3.5" /> Go private
              </button>
            )}
            <button
              data-testid="radio-caller-talk-back"
              onClick={toggleHostMic}
              title={hostMicStream ? "Stop talking to caller" : "Talk to caller through this peer"}
              className={`text-[10px] font-black px-2 py-1.5 rounded-full inline-flex items-center gap-1 ${
                hostMicStream ? "bg-emerald-500 text-black" : "bg-white/5 text-white/80 hover:bg-white/10"
              }`}
            >
              {hostMicStream ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
              {hostMicStream ? "Talking" : "Talk back"}
            </button>
            <button
              data-testid="radio-caller-demote"
              onClick={() => act(onair.id, "demote")}
              disabled={busy === onair.id}
              className="ml-auto bg-rose-500/20 hover:bg-rose-500/40 text-rose-100 text-[10px] font-black px-2 py-1.5 rounded-full inline-flex items-center gap-1"
            >
              <PhoneOff className="w-3.5 h-3.5" /> Hang up
            </button>
          </div>
        </div>
      )}

      {/* Waiting list */}
      {queue.length === 0 ? (
        <div className="text-white/40 text-xs text-center py-4">No callers waiting. Drop the link in chat to invite some.</div>
      ) : (
        <div className="space-y-1.5 max-h-[360px] overflow-y-auto" data-testid="radio-caller-list">
          {queue.map((c, i) => (
            <div key={c.id} className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg p-2">
              <div className="w-7 text-center font-black text-white/40 text-xs tabular-nums">#{i + 1}</div>
              {c.user_avatar
                ? <img src={c.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                : <div className="w-8 h-8 rounded-full bg-white/10" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-white text-xs">{c.user_name}</span>
                  {c.priority > 0 && (
                    <span data-testid={`radio-caller-tipped-${c.id}`} className="text-[8px] uppercase tracking-wider font-black bg-tv-accent text-black px-1 py-px rounded">tipped $5</span>
                  )}
                </div>
                <div className="text-[10px] text-white/55 line-clamp-1">
                  {c.city ? `${c.city} · ` : ""}{c.topic || "—"}
                </div>
              </div>
              <button
                data-testid={`radio-caller-promote-${c.id}`}
                onClick={() => act(c.id, "promote")}
                disabled={busy === c.id}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-[10px] font-black px-2 py-1.5 rounded-full flex items-center gap-1"
              >
                {busy === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronUp className="w-3 h-3" />} On air
              </button>
              <button
                data-testid={`radio-caller-drop-${c.id}`}
                onClick={() => act(c.id, "drop")}
                disabled={busy === c.id}
                className="text-white/40 hover:text-rose-300 text-[10px] font-bold p-1.5"
                title="Drop from queue"
              >✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
